// ============================================================================
// Entwurfsspeicher — der Notnagel im localStorage (SPEC §5.2.2 Regel 1, §5.2.3).
//
// Vorlage: /opt/entwasserungsrechner/frontend/js/zustand.js (entwurfLesen /
// entwurfSchreiben / entwurfLoeschen / entwuerfeAufraeumen). Der UMSCHLAG ist
// dort und hier derselbe: { version, gespeichert_am, daten }. Die `version` ist
// die des SERVERS, gegen die der Entwurf entstanden ist — nur damit kann §5.2.3
// beim Öffnen entscheiden, ob der Entwurf noch gilt (gleiche Version) oder
// verworfen wird (Server ist weiter).
//
// Schlüssel: `zp.entwurf.<planId>` und `zp.zoom.<planId>` (Anhang A.10). Das
// `zp.` setzt lib/storage.ts; hier steht darum nur der Rest. Der Zoom liegt
// bewusst NEBEN dem Entwurf und nicht im Plan (T25, B5): er ist eine
// Eigenschaft des Arbeitsplatzes, nicht des Dokuments — und weil er dieselbe
// Ablage benutzt, steht das Wissen über die Schlüsselnamen an EINER Stelle.
//
// Jeder Zugriff ist in try/catch: im privaten Fenster, bei vollem Kontingent
// und bei gesperrten Website-Daten wirft schon das bloße Lesen.
// ============================================================================
import type { Plan } from "@kern";
import { TX } from "./texte";
import { toast } from "../store/toasts";
import { load, remove, save } from "./storage";

/** Wie lange ein Entwurf zu einem Plan liegen bleibt, den niemand mehr öffnet. */
export const ENTWURF_TAGE = 30;

/** Der Umschlag im localStorage. `version` ist die SERVER-Version (§5.2.3). */
export interface Entwurf {
  version: number;
  /** ISO-Zeitstempel des letzten Tippens. */
  gespeichert_am: string;
  daten: Plan;
}

const ENTWURF_SCHLUESSEL = (planId: string) => "entwurf." + planId;
const ZOOM_SCHLUESSEL = (planId: string) => "zoom." + planId;

/** Voller Schlüssel im localStorage, mit dem Präfix aus lib/storage.ts. */
const ROH_PRAEFIX = "zp.entwurf.";

/** Der Umschlag oder `null` — nie ein halb gelesenes Objekt. */
export function entwurfLesen(planId: string): Entwurf | null {
  const e = load<Entwurf | null>(ENTWURF_SCHLUESSEL(planId), null);
  if (!e || typeof e !== "object") return null;
  if (typeof e.version !== "number" || !e.daten || typeof e.daten !== "object") return null;
  return e;
}

/** Nur EINMAL je Sitzung melden — sonst kommt der Satz alle 1,5 s. */
let vollGemeldet = false;

/**
 * Entwurf ablegen. Wird bei JEDEM `aendern` gerufen (§5.2.2 Regel 1).
 *
 * Hier steht ABSICHTLICH nicht `save()` aus lib/storage.ts: das schluckt jeden
 * Fehler still. Der Notnagel darf aber nicht genau dann lautlos ausfallen,
 * wenn er gebraucht wird — bei vollem Kontingent oder im privaten Fenster muss
 * der Mensch erfahren, dass er selbst sichern soll (TX.sicherung.entwurfFehlt).
 * Geworfen wird trotzdem nie: ein fehlgeschlagener Entwurf darf den Editor
 * nicht anhalten.
 */
export function entwurfSchreiben(planId: string, version: number, daten: Plan): void {
  const umschlag: Entwurf = { version, gespeichert_am: new Date().toISOString(), daten };
  try {
    localStorage.setItem(ROH_PRAEFIX + planId, JSON.stringify(umschlag));
    vollGemeldet = false;
  } catch {
    if (!vollGemeldet) {
      vollGemeldet = true;
      toast.warn(TX.sicherung.entwurfFehlt, { dauer: 0 });
    }
  }
}

/** Nach erfolgreichem PUT und nach `serverstandLaden()` (§5.2.2 Regel 10). */
export function entwurfLoeschen(planId: string): void {
  remove(ENTWURF_SCHLUESSEL(planId));
}

/** Alle Entwurfsschlüssel im localStorage — mit vollem Präfix. */
function entwurfSchluessel(): string[] {
  const raus: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(ROH_PRAEFIX)) raus.push(k);
    }
  } catch {
    /* gesperrt */
  }
  return raus;
}

/**
 * Entwürfe verwerfen, deren Plan es nicht mehr gibt, und alte.
 * Wird von der Planliste mit den bekannten Kennungen gerufen.
 */
export function entwuerfeAufraeumen(bekannteIds: Iterable<string>): void {
  const bekannt = new Set<string>();
  for (const id of bekannteIds) bekannt.add(String(id));
  const grenze = Date.now() - ENTWURF_TAGE * 86400000;
  for (const k of entwurfSchluessel()) {
    const id = k.slice(ROH_PRAEFIX.length);
    let alt = 0;
    try {
      alt = Date.parse(String(JSON.parse(localStorage.getItem(k) ?? "{}").gespeichert_am));
    } catch {
      alt = 0;
    }
    if (!bekannt.has(id) || !(alt > grenze)) {
      try {
        localStorage.removeItem(k);
      } catch {
        /* gesperrt */
      }
    }
  }
}

/**
 * Alle Entwürfe verwerfen — beim ABMELDEN, nicht beim 401.
 * Auf einem geteilten Rechner soll der Stand des zuletzt bearbeiteten Plans
 * nicht für die nächste Person lesbar bleiben. Bei einem 401 bleiben sie
 * ausdrücklich stehen (§5.2.2 Regel 6).
 */
export function alleEntwuerfeVerwerfen(): void {
  for (const k of entwurfSchluessel()) {
    try {
      localStorage.removeItem(k);
    } catch {
      /* gesperrt */
    }
  }
}

/** Gemerkter Zoom des Arbeitsplatzes, `null` wenn keiner abgelegt ist (§5.6.2). */
export function zoomLesen(planId: string): number | null {
  const z = load<number | null>(ZOOM_SCHLUESSEL(planId), null);
  return typeof z === "number" && Number.isFinite(z) && z > 0 ? z : null;
}

export function zoomSchreiben(planId: string, zoom: number): void {
  save(ZOOM_SCHLUESSEL(planId), zoom);
}
